import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Database, CheckCircle2, FileText, Users, Tag, MapPin, Mail, Clock, Copy, Check, HistoryIcon } from 'lucide-react';
import { campaignsApi, CampaignRecord, formVersionsApi, FormVersionRecord } from '../services/creatorApi';
import { VersionHistoryPanel } from '../components/VersionHistoryPanel';
import { auditService } from '../services/auditService';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function CampaignDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const [campaign, setCampaign] = useState<CampaignRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  useEffect(() => {
    const loadCampaign = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await campaignsApi.get(id);
        setCampaign(data);
      } catch (err) {
        console.error('Failed to load campaign:', err);
        toast.error('Failed to load campaign');
      } finally {
        setLoading(false);
      }
    };
    loadCampaign();
  }, [id]);
  const handleCopy = (text: string, itemId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(itemId);
    setTimeout(() => setCopiedItem(null), 2000);
  };
  const handleRestoreVersion = async (version: FormVersionRecord | any) => {
    const formVersion = version as FormVersionRecord;
    if (!campaign || !id) return;
    try {
      let restoredData: any = {};
      if (formVersion.Builder_Schema) {
        try {
          restoredData = JSON.parse(formVersion.Builder_Schema);
        } catch {
          // Ignore parse errors
        }
      }
      const payload = {
        Email_Subject: restoredData.Email_Subject || campaign.Email_Subject,
        Preheader_Text: restoredData.Preheader_Text || campaign.Preheader_Text,
        From_Name: restoredData.From_Name || campaign.From_Name,
        From_Email: restoredData.From_Email || campaign.From_Email,
        HTML_Content: restoredData.HTML_Content || formVersion.HTML || campaign.HTML_Content,
        Text_Content: restoredData.Text_Content || campaign.Text_Content,
        Status: restoredData.Status || campaign.Status
      };
      await campaignsApi.update(id, payload);
      // Update local state
      setCampaign({
        ...campaign,
        ...payload
      });
      auditService.log({
        actionType: 'restored',
        entityType: 'campaign',
        entityId: id,
        entityName: campaign.Campaign_Name,
        summary: `Restored to Version ${formVersion.Version_Number}`,
        userName: currentUser.User_Name,
        userEmail: currentUser.Email,
        userRole: currentUser.Role,
        team: currentWorkspace.name,
        region: currentSubRegion || ''
      });
    } catch (err) {
      console.error('Failed to restore campaign version:', err);
      throw err;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Sent':
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700 border border-green-200" data-id="element-3151" data-mp="3dxmh7fr8frlc">
            <CheckCircle2 className="h-4 w-4" data-id="element-3152" data-mp="38yjnuskm79t7" />
            Sent
          </span>;
      case 'Exported':
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-purple-50 text-purple-700 border border-purple-200" data-id="element-3153" data-mp="3iwzfcwnk0l38">
            <Database className="h-4 w-4" data-id="element-3154" data-mp="1tikuinfnpid0" />
            Exported
          </span>;
      default:
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-3155" data-mp="1ol18v2gmq3b1">
            <FileText className="h-4 w-4" data-id="element-3156" data-mp="kk1jdjckd3r2" />
            Draft
          </span>;
    }
  };
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-3157" data-mp="1s8otkhujah8">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3158" data-mp="5unaeccwgoc7" />
      </div>;
  }
  if (!campaign) {
    return <div className="max-w-3xl mx-auto text-center py-16" data-id="element-3159" data-mp="ap19aoqrtdlm">
        <FileText className="mx-auto h-12 w-12 text-gray-400" data-id="element-3160" data-mp="3a9dcm94u0bu5" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900" data-id="element-3161" data-mp="3ebrryj9aktgo">
          Campaign not found
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-3162" data-mp="36t0nsc5fejbo">
          This campaign may have been deleted.
        </p>
        <Link to="/campaigns" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-3163" data-mp="31t5wi7hqnrq3">
          <ArrowLeft className="h-4 w-4" data-id="element-3164" data-mp="2st94bvw54aqa" />
          Back to Campaigns
        </Link>
      </div>;
  }
  return <div className="max-w-3xl mx-auto space-y-6 pb-12" data-id="element-3165" data-mp="2nuoduo6ox5f1">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3166" data-mp="2j368zp1jqkos">
        <div className="flex items-center gap-4" data-id="element-3167" data-mp="2j1le1ybbkzqe">
          <button onClick={() => navigate('/campaigns')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md" data-id="element-3168" data-mp="2e31ug4k78017">
            <ArrowLeft className="h-5 w-5" data-id="element-3169" data-mp="2535a2f4udnby" />
          </button>
          <div data-id="element-3170" data-mp="252lpeqnt4ku8">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3171" data-mp="2557wyqap70cg">
              {campaign.Campaign_Name || 'Untitled Campaign'}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-3172" data-mp="1qg6lqt1a791g">
              ID:{' '}
              <code className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-3173" data-mp="1vf9izrb0sayv">
                {campaign.Campaign_ID}
              </code>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-3174" data-mp="1lhd9f3xqdo2v">
          <button onClick={() => setShowVersionHistory(true)} className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-3175" data-mp="1li4sxge1zvcs">
            <HistoryIcon className="h-4 w-4" data-id="element-3176" data-mp="1zh4fmii0a06b" /> History
          </button>
          {getStatusBadge(campaign.Status)}
        </div>
      </div>

      {/* Campaign Details */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-200" data-id="element-3177" data-mp="24eec2i3tbc3n">
        <div className="p-6" data-id="element-3178" data-mp="2s3p6ziyhec9v">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3179" data-mp="2ww8milgqbhhb">
            Email Details
          </h2>
          <div className="grid grid-cols-2 gap-y-4 gap-x-8" data-id="element-3180" data-mp="lg8efh6y4s8a">
            <div data-id="element-3181" data-mp="giq1lfpt5sv0">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-3182" data-mp="gj88d6a3jph6">
                Subject
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-3183" data-mp="v61dxjttdgw6">
                {campaign.Email_Subject || '-'}
              </span>
            </div>
            <div data-id="element-3184" data-mp="2inqccc09xoq">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-3185" data-mp="2i5jklrpw12k">
                Preheader
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-3186" data-mp="3tq040hdojszw">
                {campaign.Preheader_Text || '-'}
              </span>
            </div>
            <div data-id="element-3187" data-mp="7hrxyyja7ncy">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-3188" data-mp="7h8o853sc5a0">
                From Name
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-3189" data-mp="1mxhao6p34k04">
                {campaign.From_Name || '-'}
              </span>
            </div>
            <div data-id="element-3190" data-mp="1iz9q10ri2yjn">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-3191" data-mp="3cfwb3rjbb8t3">
                From Email
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-3192" data-mp="3hdxphz5smjy8">
                {campaign.From_Email || '-'}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6" data-id="element-3193" data-mp="3r4ady2gqax9n">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3194" data-mp="3r5bz4zrgl053">
            Export Details
          </h2>
          <div className="grid grid-cols-2 gap-y-4 gap-x-8" data-id="element-3195" data-mp="3v889y4arbryh">
            <div data-id="element-3196" data-mp="3yjcdgjrlc7m">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-3197" data-mp="3xzt9widohs0">
                Recipients
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block font-medium" data-id="element-3198" data-mp="8x30nnltz035">
                {campaign.Recipient_Count ? campaign.Recipient_Count.toLocaleString() : '-'}
              </span>
            </div>
            <div data-id="element-3199" data-mp="2fqevokuj28e7">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-3200" data-mp="2fpx2xy72qcb1">
                Exported At
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-3201" data-mp="wwde7pizbpew">
                {formatDate(campaign.Exported_At)}
              </span>
            </div>
            {campaign.Bucket_ID && <div className="col-span-2" data-id="element-3202" data-mp="nzuoem61ohmj">
                <span className="text-xs font-medium text-gray-500 block mb-1" data-id="element-3203" data-mp="j3dzvquxo18x">
                  Bucket ID
                </span>
                <div className="flex items-center gap-2" data-id="element-3204" data-mp="1frtu408zmysd">
                  <code className="text-sm font-mono bg-purple-50 text-purple-700 px-3 py-1.5 rounded border border-purple-100" data-id="element-3205" data-mp="1brt3lh8m6x4y">
                    {campaign.Bucket_ID}
                  </code>
                  <button onClick={() => handleCopy(campaign.Bucket_ID, 'bucket')} className="text-gray-400 hover:text-gray-600 p-1.5 hover:bg-gray-100 rounded" title="Copy Bucket ID" data-id="element-3206" data-mp="11tlybvnou3is">
                    {copiedItem === 'bucket' ? <Check className="h-4 w-4 text-green-500" data-id="element-3207" data-mp="um3dn5m7hxp4" /> : <Copy className="h-4 w-4" data-id="element-3208" data-mp="pobmidtdyflz" />}
                  </button>
                </div>
              </div>}
            {campaign.Bucket_Expires_At && <div data-id="element-3209" data-mp="zlfzpo5970qn">
                <span className="text-xs font-medium text-gray-500 block" data-id="element-3210" data-mp="zky66z9z70mf">
                  Bucket Expires
                </span>
                <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-3211" data-mp="1dbjha1t6u69r">
                  {campaign.Bucket_Expires_At}
                </span>
              </div>}
          </div>
        </div>

        <div className="p-6" data-id="element-3212" data-mp="1nasgb92wub2z">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3213" data-mp="1ich5ow4sjwjp">
            Scope
          </h2>
          <div className="grid grid-cols-2 gap-y-4 gap-x-8" data-id="element-3214" data-mp="3p25bh6f2t4lu">
            <div data-id="element-3215" data-mp="3p4rpnowh2id1">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-3216" data-mp="3k43tkotdzbby">
                Team
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-3217" data-mp="1zlji31zdj21e">
                {campaign.Team || '-'}
              </span>
            </div>
            <div data-id="element-3218" data-mp="24mz2crtq94iq">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-3219" data-mp="1pvzxnrw0j04f">
                Region
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-3220" data-mp="1uut9lfvnij2h">
                {campaign.Region || 'All'}
              </span>
            </div>
          </div>
        </div>

        {/* Targeting */}
        {(campaign.Campaign_Target_Regions?.length || campaign.Campaign_Target_Mailing_Lists?.length || campaign.Campaign_Target_Tags?.length) && <div className="p-6" data-id="element-3221" data-mp="16zgx3qz9ykjh">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3222" data-mp="1bvp8ctacptld">
              Targeting
            </h2>
            <div className="space-y-4" data-id="element-3223" data-mp="31bojgfxj5t3n">
              {campaign.Campaign_Target_Regions && campaign.Campaign_Target_Regions.length > 0 && <div data-id="element-3224" data-mp="34tm383n5x6ki">
                    <span className="text-xs font-medium text-gray-500 flex items-center gap-1 mb-2" data-id="element-3225" data-mp="34tug3vjacbvp">
                      <MapPin className="h-3 w-3" data-id="element-3226" data-mp="2zt6rthmfy956" /> Regions
                    </span>
                    <div className="flex flex-wrap gap-2" data-id="element-3227" data-mp="2uvy471iixcwj">
                      {campaign.Campaign_Target_Regions.map((r, i) => <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-3228" data-mp="2pxmyn2z9hok4" data-mpi={i}>
                          {r.Region_Name}
                        </span>)}
                    </div>
                  </div>}
              {campaign.Campaign_Target_Mailing_Lists && campaign.Campaign_Target_Mailing_Lists.length > 0 && <div data-id="element-3229" data-mp="5dfnc8dqkwvg">
                    <span className="text-xs font-medium text-gray-500 flex items-center gap-1 mb-2" data-id="element-3230" data-mp="5cwedh62ditk">
                      <Mail className="h-3 w-3" data-id="element-3231" data-mp="dnwtnik1aft9" /> Mailing Lists
                    </span>
                    <div className="flex flex-wrap gap-2" data-id="element-3232" data-mp="hp89wxh0poos">
                      {campaign.Campaign_Target_Mailing_Lists.map((l, i) => <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-3233" data-mp="mpcryrxkqrh4" data-mpi={i}>
                          {l.Mailing_List_Name || l.Mailing_List_ID}
                        </span>)}
                    </div>
                  </div>}
              {campaign.Campaign_Target_Tags && campaign.Campaign_Target_Tags.length > 0 && <div data-id="element-3234" data-mp="1fcqkmdu8p0vf">
                    <span className="text-xs font-medium text-gray-500 flex items-center gap-1 mb-2" data-id="element-3235" data-mp="1fdiftnjxcwl6">
                      <Tag className="h-3 w-3" data-id="element-3236" data-mp="1kbjzoap6czxp" /> Tags
                    </span>
                    <div className="flex flex-wrap gap-2" data-id="element-3237" data-mp="2h1dpjoorpveg">
                      {campaign.Campaign_Target_Tags.map((t, i) => <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-3238" data-mp="2c183co19p13r" data-mpi={i}>
                          {t.Tag_Name}
                        </span>)}
                    </div>
                  </div>}
            </div>
          </div>}

        {/* Content Preview */}
        {(campaign.HTML_Content || campaign.Text_Content) && <div className="p-6" data-id="element-3239" data-mp="232e9v6j8gquf">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-3240" data-mp="2312x4h7t88r6">
              Content
            </h2>
            {campaign.Text_Content && <div data-id="element-3241" data-mp="3egrjb6d8p3qx">
                <span className="text-xs font-medium text-gray-500 block mb-2" data-id="element-3242" data-mp="3eh9m6lueqg7t">
                  Plain Text
                </span>
                <pre className="text-sm text-gray-700 bg-gray-50 rounded-lg p-4 border border-gray-200 whitespace-pre-wrap font-mono max-h-48 overflow-auto" data-id="element-3243" data-mp="3m3ojvs3pxcrj">
                  {campaign.Text_Content}
                </pre>
              </div>}
          </div>}
      </div>

      <VersionHistoryPanel isOpen={showVersionHistory} onClose={() => setShowVersionHistory(false)} entityType="form" entityId={id || ''} entityName={campaign.Campaign_Name || 'Campaign'} onRestore={handleRestoreVersion} data-id="element-3244" data-mp="3h4brkru2vdhk" />
    </div>;
}